/**
 * @file userApi.js
 * @description 用户域 API（入参/出参与存量保持一致）
 * @module features/user/api/userApi
 */

import axios from '@/shared/utils/axios'
import API from '@/config/api'
import { getAuthToken } from '@/shared/utils/auth'

/**
 * 查询当月签到记录（用于连续签到天数）
 * @param {{ clockYear: number, clockMonth: number }} payload
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchClockRecordApi(payload) {
  const token = getAuthToken()
  return axios.post(API.USER.CLOCK_RECORD, payload, {
    headers: { 'COMIC-WEB-TOKEN': token },
  })
}

/**
 * 当日签到
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function clockDoApi() {
  const token = getAuthToken()
  return axios.post(
    API.USER.CLOCK_DO,
    {},
    {
      headers: { 'COMIC-WEB-TOKEN': token },
    }
  )
}

/**
 * 补签
 * @param {{ makeupDate: string }} payload
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function clockMakeupApi(payload) {
  const token = getAuthToken()
  return axios.post(API.USER.CLOCK_MAKEUP, payload, {
    headers: { 'COMIC-WEB-TOKEN': token },
  })
}

/**
 * 发送短信验证码
 * @param {{ mobile: string }} payload
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function sendSmsApi(payload) {
  return axios.post(API.AUTH.SMS_SEND, payload)
}

/**
 * 登录
 * @param {{ moduleCode: string, mobile: string, verifyCode: string }} payload
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function signInApi(payload) {
  return axios.post(API.AUTH.SIGN_IN, payload)
}

/**
 * 注册
 * @param {{ mobile: string, verifyCode: string }} payload
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function signUpApi(payload) {
  return axios.post(API.AUTH.SIGN_UP, payload)
}

/**
 * 退出登录
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function signOutApi() {
  const token = getAuthToken()
  return axios.post(
    API.AUTH.SIGN_OUT,
    {},
    {
      headers: { 'COMIC-WEB-TOKEN': token },
    }
  )
}

/**
 * 获取用户基本信息
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchUserBaseApi(token = '') {
  const authToken = token || getAuthToken()
  return axios.get(API.USER.BASE, {
    headers: { 'COMIC-WEB-TOKEN': authToken },
  })
}

/**
 * 修改用户基本信息
 * @param {{ name: string, phone: string, sign: string }} payload
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function updateUserBaseApi(payload) {
  const token = getAuthToken()
  return axios.post(API.USER.BASE_MODIFIED, payload, {
    headers: { 'COMIC-WEB-TOKEN': token },
  })
}

/**
 * 上传用户头像
 * @param {FormData} formData
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function uploadUserAvatarApi(formData) {
  const token = getAuthToken()
  return axios.post(API.USER.AVATAR_UPLOAD, formData, {
    headers: { 'COMIC-WEB-TOKEN': token },
  })
}

/**
 * 修改密码
 * @param {{ oldPassword: string, newPassword: string }} payload
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function updateUserPasswordApi(payload) {
  const token = getAuthToken()
  return axios.post(API.USER.PASSWORD_MODIFIED, payload, {
    headers: { 'COMIC-WEB-TOKEN': token },
  })
}

/**
 * 金币变动记录分页
 * @param {{ pageNum: number, pageSize: number, queryMonth: string, sourceType: 'CLOCK'|'TIP'|'RECHARGE'|'CONSUME'|'GOLD_RECHARGE' }} payload sourceType：RECHARGE=橘子币充值（OpenAPI）；GOLD_RECHARGE 为旧别名
 * @returns {Promise<import('axios').AxiosResponse>}
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchGoldRecordPageApi(payload) {
  const token = getAuthToken()
  return axios.post(API.USER.GOLD_RECORD_PAGE, payload, {
    headers: { 'COMIC-WEB-TOKEN': token },
  })
}

/**
 * 收藏分页
 * @param {{ pageNum: number, pageSize: number }} params
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchUserCollectPageApi(params) {
  const token = getAuthToken()
  return axios.get(API.USER_COLLECT.PAGE, {
    headers: { 'COMIC-WEB-TOKEN': token },
    params,
  })
}

/**
 * 观看历史分页
 * @param {{ pageNum: number, pageSize: number }} params
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchUserHistoryPageApi(params) {
  const token = getAuthToken()
  return axios.get(API.USER_HISTORY.PAGE, {
    headers: { 'COMIC-WEB-TOKEN': token },
    params,
  })
}

/**
 * 收藏漫画
 * @param {string} comicId
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function insertUserCollectApi(comicId, token = '') {
  return axios.post(
    API.USER_COLLECT.INSERT,
    { comicId },
    {
      headers: { 'COMIC-WEB-TOKEN': token || getAuthToken() },
    }
  )
}

/**
 * 取消收藏
 * @param {string} comicId
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function cancelUserCollectApi(comicId, token = '') {
  return axios.post(
    API.USER_COLLECT.CANCEL,
    { comicId },
    {
      headers: { 'COMIC-WEB-TOKEN': token || getAuthToken() },
    }
  )
}

/**
 * 收藏卷（阅读页互动区）
 * @param {{ comicId: string|number, volNum: number }} payload
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function insertVolCollectApi(payload, token = '') {
  return axios.post(API.USER_COLLECT.INSERT, payload, {
    headers: { 'COMIC-WEB-TOKEN': token || getAuthToken() },
  })
}

/**
 * 取消收藏卷
 * @param {{ comicId: string|number, volNum: number }} payload
 * @param {string} [token]
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function cancelVolCollectApi(payload, token = '') {
  return axios.post(API.USER_COLLECT.CANCEL, payload, {
    headers: { 'COMIC-WEB-TOKEN': token || getAuthToken() },
  })
}

/**
 * @typedef {Object} SearchHistoryItem
 * @property {string} id
 * @property {string} keyword
 * @property {string} searchTime
 */

function searchHistoryAuthConfig(token = '') {
  return {
    headers: {
      'COMIC-WEB-TOKEN': token || getAuthToken(),
      'Content-Type': 'application/json',
    },
  }
}

/**
 * 搜索记录分页
 * @param {{ pageNum: number, pageSize: number }} payload
 * @param {string} token
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function fetchSearchHistoryPageApi(payload, token) {
  return axios.post(
    API.USER_SEARCH_HISTORY.PAGE,
    payload,
    searchHistoryAuthConfig(token)
  )
}

/**
 * 删除单条搜索记录
 * @param {string} id 记录 id
 * @param {string} token
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function deleteSearchHistoryApi(id, token) {
  return axios.post(
    API.USER_SEARCH_HISTORY.DELETE,
    { id },
    searchHistoryAuthConfig(token)
  )
}

/**
 * 清空搜索记录
 * @param {string} token
 * @returns {Promise<import('axios').AxiosResponse>}
 */
export async function clearSearchHistoryApi(token) {
  return axios.post(
    API.USER_SEARCH_HISTORY.CLEAR,
    {},
    searchHistoryAuthConfig(token)
  )
}
